import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, CalendarDays } from "lucide-react";
import { api, BACKEND } from "@/lib/api";
import { vt } from "@/lib/industry";
import { chatColors } from "@/lib/skin";

function chatSession(hid) {
  const k = `ih_chat_${hid}`;
  let s = localStorage.getItem(k);
  if (!s) {
    s = window.crypto?.randomUUID?.() || `${Date.now()}${Math.random().toString(16).slice(2)}`;
    localStorage.setItem(k, s);
  }
  return s;
}

export function ChatWidget({ houseTypeId, plotId, onBook, builderName, skin, suggestions = [], welcome, chatUrl }) {
  const cc = chatColors(skin);
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const sid = chatSession(houseTypeId);
  const end = useRef(null);

  useEffect(() => {
    api.get(`/public/chat/${sid}`, { params: { house_type_id: houseTypeId } }).then((r) => setMsgs(r.data)).catch(() => {});
  }, [sid, houseTypeId]);
  useEffect(() => { const box = end.current?.parentElement; if (box) box.scrollTop = box.scrollHeight; }, [msgs, open]);

  const send = async (e, preset) => {
    e?.preventDefault();
    const m = (preset || text).trim();
    if (!m || busy) return;
    setText("");
    setBusy(true);
    setMsgs((x) => [...x, { role: "user", content: m }, { role: "assistant", content: "" }]);
    try {
      const resp = await fetch(chatUrl || `${BACKEND}/api/public/chat`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ house_type_id: houseTypeId, plot_id: plotId || "", session_id: sid, message: m, tour_key: sessionStorage.getItem(`tourkey_${houseTypeId}`) || "" }),
      });
      const reader = resp.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        parts.forEach((p) => {
          const line = p.replace(/^data: /, "");
          if (!line || line === "[DONE]") return;
          const { t } = JSON.parse(line);
          setMsgs((x) => { const c = [...x]; c[c.length - 1] = { role: "assistant", content: c[c.length - 1].content + t }; return c; });
        });
      }
    } catch {
      setMsgs((x) => { const c = [...x]; c[c.length - 1] = { role: "assistant", content: "Sorry, I couldn't reach the assistant. Would you like to book a viewing?" }; return c; });
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} className="h-12 sm:h-14 pl-3.5 pr-4 sm:pl-4 sm:pr-5 rounded-full glass-dark shadow-2xl inline-flex items-center gap-2 font-semibold text-sm hover:opacity-90 transition-opacity" data-testid="chat-open-button">
        <MessageCircle className="w-5 h-5" /><span className="sm:hidden">Ask</span><span className="hidden sm:inline">{vt().ask}</span>
      </button>
    );
  }
  const asked = new Set(msgs.filter((m) => m.role === "user").map((m) => m.content));
  const left = suggestions.filter((q) => q && !asked.has(q));
  return (
    <div className="w-[calc(100vw-1.5rem)] sm:w-[380px] h-[70vh] sm:h-[520px] rounded-3xl glass-dark shadow-2xl flex flex-col overflow-hidden rise" data-testid="chat-panel">
      <div className="px-5 py-4 flex items-center justify-between border-b border-white/10" data-testid="chat-header">
        <div><p className="font-serif text-lg leading-tight" data-testid="chat-title">{vt().chatTitle}</p><p className="text-xs text-white/60">{builderName}</p></div>
        <button onClick={() => setOpen(false)} className="w-10 h-10 rounded-full hover:bg-white/10 grid place-items-center" data-testid="chat-close-button"><X className="w-5 h-5" /></button>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="chat-messages">
        {msgs.length === 0 && <p className="text-sm text-white/70" data-testid="chat-hello">{welcome?.trim() || vt().chatHello}</p>}
        {msgs.map((m, i) => (
          <div key={i} className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "ml-auto" : "bg-white/10 text-white"}`} style={m.role === "user" ? cc : undefined} data-testid={`chat-message-${m.role}`}>
            {m.content || <span className="animate-pulse">…</span>}
          </div>
        ))}
        <div ref={end} />
      </div>
      {left.length > 0 && <div className="px-4 pt-2 flex gap-2 overflow-x-auto no-scrollbar" data-testid="chat-suggestions">{left.map((q, i) => <button key={q} disabled={busy} onClick={() => send(null, q)} className="shrink-0 text-xs rounded-full border border-white/20 bg-white/5 text-white/80 px-3 py-1.5 hover:border-white/50 transition-colors disabled:opacity-50" data-testid={`chat-suggestion-${i}`}>{q}</button>)}</div>}
      <div className="px-4 pt-2"><button onClick={onBook} className="text-xs font-semibold text-white/80 hover:text-white inline-flex items-center gap-1" data-testid="chat-book-viewing"><CalendarDays className="w-3.5 h-3.5" />{vt().book}</button></div>
      <form onSubmit={send} className="p-3 flex gap-2">
        <input value={text} onChange={(e) => setText(e.target.value)} placeholder={vt().askHint} className="flex-1 h-11 rounded-full border border-white/15 bg-white/5 text-white placeholder:text-white/40 px-4 text-sm outline-none focus:border-white/40" data-testid="chat-input" />
        <button disabled={busy} className="w-11 h-11 rounded-full grid place-items-center disabled:opacity-50" style={cc} data-testid="chat-send-button"><Send className="w-4 h-4" /></button>
      </form>
    </div>
  );
}
